import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  HStack,
  VStack,
  Icon,
  Flex,
  Grid,
  GridItem,
} from '@chakra-ui/react';
import { FaArrowRight, FaSeedling } from 'react-icons/fa';
import { GiClout } from 'react-icons/gi';

import { MotionBox } from './motion';
import Wave from './Wave';

import cacaoImage from '../Components/Assets/cacao-arabia.jpg';
import macadamiaImage from '../Components/Assets/macadamia.jpg';
import cloutImage from '../Components/Assets/indusstrie.jpeg';

function Hero() {
  return (
    <Box position="relative" overflow="hidden" bg="#051A12" color="white" minH={{ base: 'auto', lg: '90vh' }}>
      {/* Diagonal split background */}
      <Box position="absolute" inset={0}>
        <Box
          position="absolute"
          top={0}
          left={0}
          right={{ base: 0, lg: '45%' }}
          bottom={0}
          bgGradient="linear(160deg, #062A1B 0%, #051A12 100%)"
        />
        <Box
          position="absolute"
          top={0}
          right={0}
          left={{ base: 0, lg: '55%' }}
          bottom={0}
          display={{ base: 'none', lg: 'block' }}
        >
          <Box
            position="absolute"
            inset={0}
            bgImage={`url(${cacaoImage})`}
            bgSize="cover"
            bgPosition="center"
          />
          <Box
            position="absolute"
            inset={0}
            bgGradient="linear(270deg, rgba(5,26,18,0.95) 0%, rgba(5,26,18,0.65) 40%, transparent 100%)"
          />
        </Box>
        {/* Mobile background */}
        <Box
          position="absolute"
          inset={0}
          display={{ base: 'block', lg: 'none' }}
          bgImage={`url(${cacaoImage})`}
          bgSize="cover"
          bgPosition="center"
          opacity={0.18}
        />
        <Box
          position="absolute"
          inset={0}
          display={{ base: 'block', lg: 'none' }}
          bgGradient="linear(180deg, rgba(5,26,18,0.98) 0%, rgba(5,26,18,0.88) 100%)"
        />
        <Box position="absolute" inset={0} className="bg-grid" opacity={0.16} />
      </Box>

      {/* Decorative angle line */}
      <Box
        position="absolute"
        top={0}
        right={{ base: '30%', lg: '45%' }}
        bottom={0}
        width="2px"
        bgGradient="linear(to bottom, transparent, rgba(201,147,31,0.4), transparent)"
        display={{ base: 'none', lg: 'block' }}
        transform="skewX(-12deg)"
      />

      <Container maxW="7xl" position="relative" zIndex={2} pt={{ base: 28, md: 32 }} pb={{ base: 22, md: 28 }}>
        <Grid templateColumns={{ base: '1fr', lg: '55% 45%' }} gap={0} alignItems="center">
          <GridItem pr={{ base: 0, lg: 16 }}>
            <VStack align="start" spacing={{ base: 7, md: 9 }}>
              <HStack spacing={3}>
                <Box w="40px" h="1px" bg="gold.400" />
                <Text fontSize="xs" fontWeight="700" letterSpacing="0.24em" textTransform="uppercase" color="gold.300">
                  Agro-industrie congolaise
                </Text>
              </HStack>

              <Heading
                as="h1"
                fontSize={{ base: '3xl', sm: '4xl', md: '5xl', xl: '6xl' }}
                fontWeight="900"
                lineHeight="0.95"
                letterSpacing="-0.05em"
              >
                De la terre fertile
                <Text as="span" display="block" mt={2} color="whiteAlpha.900">
                  à la valeur ajoutée
                </Text>
              </Heading>

              <Text fontSize={{ base: 'md', md: 'lg' }} color="whiteAlpha.800" lineHeight="1.9" maxW="xl">
                TRIMER SARL intègre la production agricole et la transformation industrielle
                en République Démocratique du Congo. Nous cultivons cacao et macadamia,
                et produisons des clous métalliques localement pour réduire la dépendance
                aux importations.
              </Text>

              <HStack spacing={4} flexWrap="wrap">
                <Button
                  as={RouterLink}
                  to="/agro-business"
                  size="lg"
                  bg="gold.500"
                  color="navy.900"
                  _hover={{ bg: 'gold.400', boxShadow: '0 20px 60px -40px rgba(233,196,104,0.9)' }}
                  rightIcon={<FaArrowRight />}
                  px={9}
                >
                  Explorer nos pôles
                </Button>
                <Button
                  as={RouterLink}
                  to="/industries"
                  size="lg"
                  variant="outline"
                  borderColor="whiteAlpha.400"
                  color="white"
                  _hover={{ bg: 'whiteAlpha.100' }}
                  px={9}
                >
                  Voir la clouterie
                </Button>
              </HStack>

              {/* Three pillars */}
              <Grid templateColumns={{ base: '1fr', sm: 'repeat(3, 1fr)' }} gap={6} w="full" pt={{ base: 4, md: 8 }}>
                {[
                  { icon: FaSeedling, title: 'Agriculture', desc: 'Cacao & Macadamia' },
                  { icon: GiClout, title: 'Industrie', desc: '160 000 clous/jour' },
                  { icon: FaSeedling, title: 'Intégration', desc: "De la matière au produit" },
                ].map((item) => (
                  <MotionBox
                    key={item.title}
                    p={5}
                    borderRadius="xl"
                    border="1px solid"
                    borderColor="whiteAlpha.200"
                    bg="whiteAlpha.100"
                    backdropFilter="blur(10px)"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                  >
                    <VStack align="start" spacing={2}>
                      <Icon as={item.icon} color="gold.300" boxSize={5} />
                      <Text fontWeight="800" fontSize="lg">
                        {item.title}
                      </Text>
                      <Text fontSize="sm" color="whiteAlpha.700">
                        {item.desc}
                      </Text>
                    </VStack>
                  </MotionBox>
                ))}
              </Grid>
            </VStack>
          </GridItem>

          <GridItem display={{ base: 'none', lg: 'block' }} position="relative" h="640px">
            <MotionBox
              position="absolute"
              top="10%"
              right="8%"
              w="58%"
              h="42%"
              borderRadius="2xl"
              overflow="hidden"
              boxShadow="0 100px 160px -120px rgba(0,0,0,0.95)"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            >
              <Box w="full" h="full" bgImage={`url(${macadamiaImage})`} bgSize="cover" bgPosition="center" />
            </MotionBox>
            <MotionBox
              position="absolute"
              bottom="12%"
              left="6%"
              w="64%"
              h="48%"
              borderRadius="2xl"
              overflow="hidden"
              boxShadow="0 120px 180px -140px rgba(0,0,0,0.98)"
              border="10px solid rgba(5,26,18,0.9)"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            >
              <Box w="full" h="full" bgImage={`url(${cloutImage})`} bgSize="cover" bgPosition="center" />
            </MotionBox>

            {/* Badge */}
            <Flex
              position="absolute"
              top="48%"
              left="38%"
              bg="rgba(5,26,18,0.9)"
              backdropFilter="blur(16px)"
              border="1px solid"
              borderColor="whiteAlpha.300"
              borderRadius="full"
              px={5}
              py={2}
              alignItems="center"
              gap={3}
              boxShadow="0 60px 100px -80px rgba(0,0,0,0.9)"
            >
              <Box w="8px" h="8px" borderRadius="full" bg="gold.400" className="animate-pulse-ring" />
              <Text fontSize="sm" fontWeight="700">
                Intégration verticale
              </Text>
            </Flex>
          </GridItem>
        </Grid>
      </Container>

      <Wave fill="white" height={70} />
    </Box>
  );
}

export default Hero;
